import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, PenLine, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formularioPublicoService } from '@/services/formulariosApi';
import { Formulario, FormularioCampo } from '@/types';

function opcoesDe(campo: FormularioCampo): string[] {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return Array.isArray(cfg.opcoes) ? (cfg.opcoes as string[]) : [];
}

function escalaDe(campo: FormularioCampo): { min: number; max: number } {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return {
    min: typeof cfg.min === 'number' ? cfg.min : 1,
    max: typeof cfg.max === 'number' ? cfg.max : 5,
  };
}

function permiteOutroDe(campo: FormularioCampo): boolean {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return cfg.permiteOutro === true;
}

const OUTRO_VALOR = '__outro__';

function erroDeData(tipo: 'data' | 'hora' | 'data_hora', valor: string): string | null {
  if (!valor) return null;
  if (tipo === 'hora') {
    const m = /^(\d{2}):(\d{2})$/.exec(valor);
    if (!m) return 'Horário em formato inválido.';
    const [, h, min] = m;
    if (Number(h) > 23 || Number(min) > 59) return 'Horário inexistente.';
    return null;
  }
  const dataParte = tipo === 'data' ? valor : valor.split('T')[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dataParte);
  if (!m) return 'Data em formato inválido.';
  const [, ano, mes, dia] = m;
  const d = new Date(Number(ano), Number(mes) - 1, Number(dia));
  const valida = d.getFullYear() === Number(ano) && d.getMonth() === Number(mes) - 1 && d.getDate() === Number(dia);
  if (!valida) return 'Essa data não existe no calendário.';
  return null;
}

function valorVazio(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

function CampoPublico({
  campo, valor, outroTexto, onChange, onOutroTextoChange,
}: {
  campo: FormularioCampo;
  valor: unknown;
  outroTexto: string;
  onChange: (v: unknown) => void;
  onOutroTextoChange: (texto: string) => void;
}) {
  const label = (
    <Label className="flex items-center gap-1">
      {campo.label}
      {campo.obrigatorio && <span className="text-destructive">*</span>}
    </Label>
  );

  switch (campo.tipo) {
    case 'texto_curto':
      return <div className="space-y-2">{label}<Input value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'texto_longo':
      return <div className="space-y-2">{label}<Textarea rows={3} className="text-left" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'numero':
      return <div className="space-y-2">{label}<Input type="number" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'data': {
      const erro = erroDeData('data', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="date" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'hora': {
      const erro = erroDeData('hora', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="time" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'data_hora': {
      const erro = erroDeData('data_hora', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="datetime-local" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'lista':
      return (
        <div className="space-y-2">
          {label}
          <Select value={(valor as string) || undefined} onValueChange={onChange}>
            <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              {opcoesDe(campo).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    case 'selecao_unica': {
      const outroAtivo = permiteOutroDe(campo);
      return (
        <div className="space-y-2">
          {label}
          <RadioGroup value={(valor as string) || ''} onValueChange={onChange}>
            {opcoesDe(campo).map((o) => (
              <div key={o} className="flex items-center gap-2">
                <RadioGroupItem value={o} id={`${campo.id}-${o}`} />
                <Label htmlFor={`${campo.id}-${o}`} className="font-normal cursor-pointer">{o}</Label>
              </div>
            ))}
            {outroAtivo && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value={OUTRO_VALOR} id={`${campo.id}-outro`} />
                <Label htmlFor={`${campo.id}-outro`} className="font-normal cursor-pointer">Outro</Label>
              </div>
            )}
          </RadioGroup>
          {outroAtivo && valor === OUTRO_VALOR && (
            <Input placeholder="Digite sua resposta..." value={outroTexto} onChange={(e) => onOutroTextoChange(e.target.value)} className="mt-1" />
          )}
        </div>
      );
    }
    case 'multipla_escolha': {
      const selecionadas = Array.isArray(valor) ? (valor as string[]) : [];
      const outroAtivo = permiteOutroDe(campo);
      return (
        <div className="space-y-2">
          {label}
          <div className="space-y-2">
            {opcoesDe(campo).map((o) => (
              <div key={o} className="flex items-center gap-2">
                <Checkbox
                  id={`${campo.id}-${o}`}
                  checked={selecionadas.includes(o)}
                  onCheckedChange={(checked) => {
                    onChange(checked ? [...selecionadas, o] : selecionadas.filter((s) => s !== o));
                  }}
                />
                <Label htmlFor={`${campo.id}-${o}`} className="font-normal cursor-pointer">{o}</Label>
              </div>
            ))}
            {outroAtivo && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${campo.id}-outro`}
                  checked={selecionadas.includes(OUTRO_VALOR)}
                  onCheckedChange={(checked) => {
                    onChange(checked ? [...selecionadas, OUTRO_VALOR] : selecionadas.filter((s) => s !== OUTRO_VALOR));
                  }}
                />
                <Label htmlFor={`${campo.id}-outro`} className="font-normal cursor-pointer">Outro</Label>
              </div>
            )}
          </div>
          {outroAtivo && selecionadas.includes(OUTRO_VALOR) && (
            <Input placeholder="Digite sua resposta..." value={outroTexto} onChange={(e) => onOutroTextoChange(e.target.value)} className="mt-1" />
          )}
        </div>
      );
    }
    case 'escala': {
      const { min, max } = escalaDe(campo);
      const opcoes = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div className="space-y-2">
          {label}
          <div className="flex gap-2 flex-wrap">
            {opcoes.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onChange(n)}
                className={`h-9 w-9 rounded-full border text-sm font-medium ${valor === n ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      );
    }
    case 'imagem':
    case 'arquivo':
      return (
        <div className="space-y-2">
          {label}
          <div className="border border-dashed rounded-lg p-4 flex items-center gap-2 text-sm text-muted-foreground opacity-60">
            <Upload className="h-4 w-4 shrink-0" />
            Envio de {campo.tipo === 'imagem' ? 'imagem' : 'arquivo'} ainda não está disponível pelo link público.
          </div>
        </div>
      );
    case 'assinatura':
      return (
        <div className="space-y-2">
          {label}
          <div className="border border-dashed rounded-lg p-6 flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground opacity-60">
            <PenLine className="h-5 w-5" />
            Assinatura eletrônica ainda não está disponível.
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function FormularioPublico() {
  const { token } = useParams<{ token: string }>();
  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [loading, setLoading] = useState(true);
  const [erroCarregar, setErroCarregar] = useState<string | null>(null);
  const [valores, setValores] = useState<Record<string, unknown>>({});
  const [outroTextos, setOutroTextos] = useState<Record<string, string>>({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setLoading(true);
      const res = await formularioPublicoService.getByToken(token);
      if (res.success) setFormulario(res.data);
      else setErroCarregar(res.error || 'Link inválido ou formulário indisponível');
      setLoading(false);
    })();
  }, [token]);

  const construirPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    for (const campo of formulario?.campos || []) {
      let v = valores[campo.id];
      if (v === undefined) continue;
      if (campo.tipo === 'selecao_unica' && v === OUTRO_VALOR) {
        v = { outro: true, texto: outroTextos[campo.id] || '' };
      }
      if (campo.tipo === 'multipla_escolha' && Array.isArray(v)) {
        v = (v as string[]).map((item) => (item === OUTRO_VALOR ? { outro: true, texto: outroTextos[campo.id] || '' } : item));
      }
      payload[campo.id] = v;
    }
    return payload;
  };

  const handleEnviar = async () => {
    if (!token || !formulario) return;
    const faltando = (formulario.campos || []).find((c) => c.obrigatorio && valorVazio(valores[c.id]));
    if (faltando) {
      toast.error(`O campo "${faltando.label}" é obrigatório`);
      return;
    }

    setEnviando(true);
    const res = await formularioPublicoService.submeter(token, { valores: construirPayload() });
    setEnviando(false);
    if (res.success) {
      setEnviado(true);
    } else {
      toast.error(res.error || 'Erro ao enviar resposta');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 p-6 flex items-center justify-center">
        <div className="w-full max-w-2xl space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (erroCarregar || !formulario) {
    return (
      <div className="min-h-screen bg-muted/30 p-6 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center text-muted-foreground">
            {erroCarregar || 'Formulário não encontrado.'}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="min-h-screen bg-muted/30 p-6 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center space-y-2">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <p className="font-medium">Resposta enviada com sucesso!</p>
            <p className="text-sm text-muted-foreground">Obrigado por preencher.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const campos = [...(formulario.campos || [])].sort((a, b) => a.ordem - b.ordem);

  return (
    <div className="min-h-screen bg-muted/30 p-6 flex items-center justify-center">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <CardTitle>{formulario.titulo}</CardTitle>
          {formulario.descricao && <CardDescription>{formulario.descricao}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-5">
          {campos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Este formulário ainda não tem campos.</p>
          ) : (
            campos.map((campo) => (
              <CampoPublico
                key={campo.id}
                campo={campo}
                valor={valores[campo.id]}
                outroTexto={outroTextos[campo.id] || ''}
                onChange={(v) => setValores((prev) => ({ ...prev, [campo.id]: v }))}
                onOutroTextoChange={(texto) => setOutroTextos((prev) => ({ ...prev, [campo.id]: texto }))}
              />
            ))
          )}

          {campos.length > 0 && (
            <Button className="w-full" onClick={handleEnviar} disabled={enviando}>
              {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Enviar
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
